import React, { Suspense, lazy } from 'react';
import { BrowserRouter as Router, Route, Routes } from 'react-router-dom';
import usePageTitle from './hooks/usePageTitle';
import Modal from './components/layout/Modal';
import { SocketProvider } from './context/SocketContext';

import ProtectedRoute from './components/auth/ProtectedRoute';
import EmployeeLayout from './components/layout/EmployeeLayout';
import EmployeeDashboard from './pages/employee/EmployeeDashboard';
import MyAttendance from './pages/employee/MyAttendance';
import MyPayslips from './pages/employee/MyPayslips';
import MyAssignments from './pages/employee/MyAssignments';
import WebLayout from './components/layout/WebLayout';
// import CMSLayout from './components/layout/CMSLayout';

// Web Pages
import Login from './pages/auth/Login';
import Signup from './pages/auth/Signup';
import ForgotPassword from './pages/auth/ForgotPassword';
import ResetPassword from './pages/auth/ResetPassword';
import VerifyOtp from './pages/auth/VerifyOtp';
import Reschedule from './pages/auth/Reschedule';
import EmailTest from './pages/auth/EmailTest';

const AdminLayout = lazy(() => import('./components/layout/AdminLayout'));
const ClientLayout = lazy(() => import('./components/layout/ClientLayout'));
const NotFound = lazy(() => import('./pages/NotFound'));

const Dashboard = lazy(() => import('./pages/admin/Dashboard'));
const Users = lazy(() => import('./pages/admin/Users'));
const Profile = lazy(() => import('./pages/admin/Profile'));
const Settings = lazy(() => import('./pages/admin/Settings'));

// Recruitment
const Applicants = lazy(() => import('./pages/admin/Applicants'));
const JobPostings = lazy(() => import('./pages/admin/JobPostings'));
const JobCategories = lazy(() => import('./pages/admin/JobCategories'));

const Pending = lazy(() => import('./pages/admin/Pending'));
const CMSLayout = lazy(() => import('./components/layout/CMSLayout'));
// const DashboardCms = lazy(() => import('./pages/cms/Dashboard'));
// const ConsoleEditor = lazy(() => import('./pages/cms/Editor'));
const CmsPage = lazy(() => import('./pages/web/CmsPage'));
const Careers = lazy(() => import('./pages/web/Careers'));
const JobDetails = lazy(() => import('./pages/web/JobDetails'));
const Contact = lazy(() => import('./pages/web/Contact'));
const Feedback = lazy(() => import('./pages/web/Feedback'));
const Documentation = lazy(() => import('./pages/web/Documentation'));

const Messages = lazy(() => import('./pages/shared/Messages'));

const Employees = lazy(() => import('./pages/admin/Employees'));
const PendingApprovals = lazy(() => import('./pages/admin/PendingApprovals'));
const PendingApprovalsDetail = lazy(() => import('./pages/admin/PendingApprovalsDetail'));
const EmployeeWorkspace = lazy(() => import('./pages/admin/employee-workspace/EmployeeWorkspace'));
const Clients = lazy(() => import('./pages/admin/Clients'));
const ClientInquiries = lazy(() => import('./pages/admin/ClientInquiries'));
const ClientLeadDetails = lazy(() => import('./pages/admin/ClientLeadDetails'));
const ClientWorkspace = lazy(() => import('./pages/admin/client-workspace/ClientWorkspace'));
const BillingDashboard = lazy(() => import('./pages/admin/BillingDashboard'));
const ClientAnalyticsDashboard = lazy(() => import('./pages/admin/ClientAnalyticsDashboard'));
const LmsCourses = lazy(() => import('./pages/admin/LmsCourses'));
const LmsLessons = lazy(() => import('./pages/admin/LmsLessons'));
const LmsEnrollments = lazy(() => import('./pages/admin/LmsEnrollments'));
const LmsEnrollmentsDetail = lazy(() => import('./pages/admin/LmsEnrollmentsDetail'));
const ComplianceDashboard = lazy(() => import('./pages/admin/ComplianceDashboard'));
const ComplianceDetail = lazy(() => import('./pages/admin/ComplianceDetail'));
const FloatingBench = lazy(() => import('./pages/admin/FloatingBench'));
const Announcements = lazy(() => import('./pages/admin/Announcements'));
const OutagesDashboard = lazy(() => import('./pages/admin/OutagesDashboard'));
const OutagesDetail = lazy(() => import('./pages/admin/OutagesDetail'));

// Client Portal Pages
const ClientDashboard = lazy(() => import('./pages/client/ClientDashboard'));
const MyEmployees = lazy(() => import('./pages/client/MyEmployees'));
const ClientEmployeeDetails = lazy(() => import('./pages/client/ClientEmployeeDetails'));
const MyInvoices = lazy(() => import('./pages/client/MyInvoices'));
const PaymentSuccess = lazy(() => import('./pages/client/PaymentSuccess'));
const PaymentCancel = lazy(() => import('./pages/client/PaymentCancel'));
const ManualPayment = lazy(() => import('./pages/client/ManualPayment'));
const MyDocuments = lazy(() => import('./pages/client/MyDocuments'));
const ClientSettings = lazy(() => import('./pages/client/Settings'));
const ClientLmsCourses = lazy(() => import('./pages/client/LmsCourses'));
const ClientLmsLessons = lazy(() => import('./pages/client/LmsLessons'));
const ClientLmsEnrollments = lazy(() => import('./pages/client/LmsEnrollments'));
const ClientBench = lazy(() => import('./pages/client/ClientBench'));
const EmployeeMyDocuments = lazy(() => import('./pages/employee/MyDocuments'));
const MyCompliance = lazy(() => import('./pages/employee/MyCompliance'));
const EmployeeAnnouncements = lazy(() => import('./pages/employee/Announcements'));
const EmployeeOutages = lazy(() => import('./pages/employee/Outages'));
const EmployeeMyCourses = lazy(() => import('./pages/employee/MyCourses'));
const EmployeeCourseView = lazy(() => import('./pages/employee/CourseView'));
const AssignmentWorkspace = lazy(() => import('./pages/employee/AssignmentWorkspace'));
const MyTimesheets = lazy(() => import('./pages/client/MyTimesheets'));
const MyTimesheetsDetail = lazy(() => import('./pages/client/MyTimesheetsDetail'));
const LeaveRequests = lazy(() => import('./pages/client/LeaveRequests'));
const RecruitmentAnalytics = lazy(() => import('./pages/admin/RecruitmentAnalytics'));

const PageTitleManager = () => { usePageTitle(); return null; };
const FullPageSpinner = () => (
    <div className='page-loading-case'>
        <div className='page-loading'>
            <div className='loader-line-scale'><div></div><div></div><div></div><div></div><div></div></div>
            <h3>Loading Page...</h3>
        </div>
    </div>
);

function App() {
    return (
        <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
            <PageTitleManager />
            <Suspense fallback={<FullPageSpinner />}>
                <Routes>
                    <Route path="/feedback" element={<Feedback />} />
                    
                    <Route element={<WebLayout />}>
                        <Route path="/login" element={<Login />} />
                        <Route path="/signup" element={<Signup />} />
                        <Route path="/forgot-password" element={<ForgotPassword />} />
                        <Route path="/reset-password/:token" element={<ResetPassword />} />
                        <Route path="/reschedule/:token" element={<Reschedule />} />
                        <Route path="/verify-otp" element={<VerifyOtp />} />
                        <Route path="/email-test" element={<EmailTest />} />

                        <Route path="/" element={<CmsPage />} />
                        <Route path="/about" element={<CmsPage />} />
                        <Route path="/careers" element={<Careers />} />
                        <Route path="/careers/:id" element={<JobDetails />} />
                        <Route path="/p/:slug" element={<CmsPage />} />
                        <Route path="/contact" element={<Contact />} />
                        <Route path="/documentation" element={<Documentation />} />
                    </Route>
                    <Route element={<ProtectedRoute allowedRoles={['admin']} />}>
                        <Route path="/admin" element={<SocketProvider token={localStorage.getItem('token')}><AdminLayout /></SocketProvider>}>
                            <Route index element={<Dashboard />} />
                            <Route path="users" element={<Users />} />
                            <Route path="employees" element={<Employees />} />
                            <Route path="attendance/pending" element={<PendingApprovals />} />
                            <Route path="attendance/pending/:id" element={<PendingApprovalsDetail />} />
                            <Route path="compliance" element={<ComplianceDashboard />} />
                            <Route path="compliance/:id" element={<ComplianceDetail />} />
                            <Route path="employees/:id/*" element={<EmployeeWorkspace />} />
                            <Route path="inquiries" element={<ClientInquiries />} />
                            <Route path="inquiries/:id" element={<ClientLeadDetails />} />
                            <Route path="clients" element={<Clients />} />
                            <Route path="clients/:id/*" element={<ClientWorkspace />} />
                            <Route path="client-analytics" element={<ClientAnalyticsDashboard />} />
                            <Route path="billing" element={<BillingDashboard />} />
                            <Route path="profile" element={<Profile />} />
                            <Route path="settings" element={<Settings />} />
                            <Route path="analytics" element={<RecruitmentAnalytics />} />
                            <Route path="applicants" element={<Applicants />} />
                            <Route path="jobs" element={<JobPostings />} />
                            <Route path="job-categories" element={<JobCategories />} />
                            <Route path="pending" element={<Pending />} />
                            <Route path="bench" element={<FloatingBench />} />
                            <Route path="announcements" element={<Announcements />} />
                            <Route path="outages" element={<OutagesDashboard />} />
                            <Route path="outages/:id" element={<OutagesDetail />} />
                            <Route path="lms-courses" element={<LmsCourses />} />
                            <Route path="lms-courses/:id/lessons" element={<LmsLessons />} />
                            <Route path="lms-enrollments" element={<LmsEnrollments />} />
                            <Route path="lms-enrollments/:id" element={<LmsEnrollmentsDetail />} />
                        </Route>
                    </Route>

                    {/* 
                    <Route element={<ProtectedRoute allowedRoles={['admin']} />}>
                        <Route path="/cms" element={<CMSLayout />}>
                            <Route index element={<DashboardCms />} />
                            <Route path="settings" element={<Settings />} />
                            <Route path="page/:page_name" element={<ConsoleEditor />} />
                        </Route>
                    </Route> 
                    */}

                    <Route element={<ProtectedRoute allowedRoles={['employee']} />}>
                        <Route path="/employee" element={<SocketProvider token={localStorage.getItem('token')}><EmployeeLayout /></SocketProvider>}>
                            <Route index element={<EmployeeDashboard />} />
                            <Route path="attendance" element={<MyAttendance />} />
                            <Route path="payslips" element={<MyPayslips />} />
                            <Route path="assignments" element={<MyAssignments />} />
                            <Route path="assignments/:id/*" element={<AssignmentWorkspace />} />
                            <Route path="messages" element={<Messages />} />
                            <Route path="documents" element={<EmployeeMyDocuments />} />
                            <Route path="compliance" element={<MyCompliance />} />
                            <Route path="announcements" element={<EmployeeAnnouncements />} />
                            <Route path="outages" element={<EmployeeOutages />} />
                            <Route path="courses" element={<EmployeeMyCourses />} />
                            <Route path="courses/:id" element={<EmployeeCourseView />} />
                            <Route path="profile" element={<Profile />} />
                        </Route>
                    </Route>
                    <Route element={<ProtectedRoute allowedRoles={['client']} />}>
                        <Route path="/client" element={<SocketProvider token={localStorage.getItem('token')}><ClientLayout /></SocketProvider>}>
                            <Route index element={<ClientDashboard />} />
                            <Route path="employees" element={<MyEmployees />} />
                            <Route path="employees/:id" element={<ClientEmployeeDetails />} />
                            <Route path="invoices" element={<MyInvoices />} />
                            <Route path="payment-success" element={<PaymentSuccess />} />
                            <Route path="payment-cancel" element={<PaymentCancel />} />
                            <Route path="manual-payment" element={<ManualPayment />} />
                            <Route path="documents" element={<MyDocuments />} />
                            <Route path="timesheets" element={<MyTimesheets />} />
                            <Route path="timesheets/:id" element={<MyTimesheetsDetail />} />
                            <Route path="leaves" element={<LeaveRequests />} />
                            <Route path="messages" element={<Messages />} />
                            <Route path="profile" element={<Profile />} />
                            <Route path="settings" element={<ClientSettings />} />
                            <Route path="lms-courses" element={<ClientLmsCourses />} />
                            <Route path="lms-courses/:id/lessons" element={<ClientLmsLessons />} />
                            <Route path="lms-enrollments" element={<ClientLmsEnrollments />} />
                            <Route path="bench" element={<ClientBench />} />
                        </Route>
                    </Route>



                    <Route element={<ProtectedRoute allowedRoles={['admin']} />}>
                        <Route path="/cms" element={<SocketProvider token={localStorage.getItem('token')}><CMSLayout /></SocketProvider>} />
                    </Route>

                    <Route path="*" element={<NotFound />} />
                </Routes>
            </Suspense>
            <Modal />
        </Router>);
}

export default App;